/**
 * Notion-like fuzzy ranked search with weighted note metadata.
 */

const getFuzzyScore = (query, text) => {
  if (!text || !query) return 0;
  const q = String(query).toLowerCase();
  const t = String(text).toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 50;

  let score = 0;
  let qIdx = 0;
  for (let i = 0; i < t.length && qIdx < q.length; i++) {
    if (t[i] === q[qIdx]) {
      score += 5;
      qIdx++;
    }
  }
  return qIdx === q.length ? score : 0;
};

const scoreNote = (query, note) => {
  let score = 0;
  score += getFuzzyScore(query, note.title) * 2;
  (note.tags || []).forEach(tag => { score += getFuzzyScore(query, tag) * 1.5; });
  score += getFuzzyScore(query, note.bodyText) * 0.6;
  (note.checklistItems || []).forEach(item => { score += getFuzzyScore(query, item.text) * 0.5; });
  (note.attachments || []).forEach(att => {
    score += getFuzzyScore(query, att.name || att.caption || att.mimeType) * 1.1;
  });

  if (score > 0) {
    if (note.favorite) score += 65;
    if (note.pinned) score += 45;
    if (note.inbox) score += 10;
    score += (note.rank || 0) * 8;
    const ageHours = Math.max(0, (Date.now() - Number(note.updatedAt || 0)) / 3600000);
    if (ageHours < 72) score += (72 - ageHours) * 0.5;
  }
  return score;
};

export const searchEngine = (query, notes, workspaces, templates) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return { notes: [], actions: [], workspaces: [] };

  const rankedNotes = (notes || [])
    .filter(note => !note.deletedAt)
    .map(note => ({ ...note, _score: scoreNote(q, note) }))
    .filter(note => note._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, 40);

  const rankedWorkspaces = (workspaces || [])
    .filter(ws => !ws.archivedAt && getFuzzyScore(q, ws.name) > 0)
    .slice(0, 8);

  const actions = [];
  const add = (id, title, icon, terms) => {
    if (terms.some(term => term.includes(q) || q.includes(term))) actions.push({ id, title, type: 'action', icon });
  };

  add('action-new-text', 'Create New Text Note', 'FileText', ['new note', 'create note', 'text note', 'new']);
  add('action-new-check', 'Create New Checklist', 'CheckSquare', ['new checklist', 'checklist', 'todo']);
  add('action-new-table', 'Create New Table', 'Grid', ['new table', 'table']);
  add('action-quick-capture', 'Open Quick Capture', 'Zap', ['quick capture', 'capture', 'paste']);
  add('action-clipboard-history', 'Open Clipboard History', 'Clipboard', ['clipboard', 'history', 'paste history']);
  add('action-sync', 'Sync Now', 'RefreshCw', ['sync', 'google sync', 'refresh']);
  add('action-focus', 'Toggle Focus Mode', 'Maximize', ['focus', 'focus mode']);
  add('nav-dashboard', 'Go to Dashboard', 'Layout', ['dashboard', 'home']);
  add('nav-settings', 'Go to Settings', 'Settings', ['settings', 'preferences']);
  add('nav-backup', 'Go to Backup Vault', 'Database', ['backup', 'vault']);
  add('nav-archive', 'View Archived Notes', 'Archive', ['archive', 'archived']);
  add('nav-favorites', 'Show Favorites', 'Heart', ['favorite', 'favorites', 'starred']);
  add('nav-recent', 'Show Recently Viewed', 'Clock', ['recent', 'recently viewed', 'history']);

  return { notes: rankedNotes, actions, workspaces: rankedWorkspaces };
};